import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { binderyApi, type ThesisBindingType, type ThesisDegreeLevel } from "@/lib/api/bindery";
import { BINDING_TYPE_LABELS, DEGREE_LEVEL_LABELS } from "../binderyFormatters";
import { ThesisPriceRuleEditor } from "./ThesisPriceRuleEditor";
import { EMPTY_PRICE_DRAFT, type PriceDraft } from "./thesis-pricing-fields";

const DEGREES: ThesisDegreeLevel[] = ["UG_FINAL", "MASTERS", "PHD"];
const BINDINGS: ThesisBindingType[] = ["SOFT", "HARD", "GOLD_EMBOSS"];

function ruleKey(degree: ThesisDegreeLevel, binding: ThesisBindingType): string {
  return `${degree}-${binding}`;
}

export default function ThesisPricingSettingsPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, PriceDraft>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await binderyApi.thesis.listPriceRules();
      const map: Record<string, PriceDraft> = {};
      data.forEach((rule) => {
        map[ruleKey(rule.degreeLevel, rule.bindingType)] = {
          basePrice: String(rule.basePrice),
          perPagePrice: String(rule.perPagePrice),
          perCopyPrice: String(rule.perCopyPrice),
          colorPagePrice: String(rule.colorPagePrice),
        };
      });
      setDrafts(map);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to load price rules"));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const draftFor = (degree: ThesisDegreeLevel, binding: ThesisBindingType): PriceDraft =>
    drafts[ruleKey(degree, binding)] ?? EMPTY_PRICE_DRAFT;

  const updateDraft = (
    degree: ThesisDegreeLevel,
    binding: ThesisBindingType,
    patch: Partial<PriceDraft>
  ) => {
    const key = ruleKey(degree, binding);
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? EMPTY_PRICE_DRAFT), ...patch },
    }));
  };

  const save = async (degree: ThesisDegreeLevel, binding: ThesisBindingType) => {
    const key = ruleKey(degree, binding);
    const draft = draftFor(degree, binding);
    setSavingKey(key);
    try {
      await binderyApi.thesis.upsertPriceRule({
        degreeLevel: degree,
        bindingType: binding,
        basePrice: Number(draft.basePrice) || 0,
        perPagePrice: Number(draft.perPagePrice) || 0,
        perCopyPrice: Number(draft.perCopyPrice) || 0,
        colorPagePrice: Number(draft.colorPagePrice) || 0,
        isActive: true,
      });
      toast.success(`${BINDING_TYPE_LABELS[binding]} prices saved`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to save price rule"));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Thesis pricing"
        description="Set what a student pays for each degree and binding. A blank amount is treated as zero."
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Thesis pricing" }]}
        showSearch={false}
        titleSize="md"
      />

      <p className="mb-6 max-w-3xl text-sm leading-6 text-slate-600">
        One copy costs the binding fee, plus the page rate for every page, plus the color surcharge
        for color pages only. That cost is multiplied by the number of copies. The extra fee, if you
        set one, is added once for each copy.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading prices…
        </div>
      ) : (
        <Tabs defaultValue="UG_FINAL">
          <TabsList className="h-auto flex-wrap">
            {DEGREES.map((degree) => (
              <TabsTrigger key={degree} value={degree}>
                {DEGREE_LEVEL_LABELS[degree]}
              </TabsTrigger>
            ))}
          </TabsList>
          {DEGREES.map((degree) => (
            <TabsContent key={degree} value={degree} className="mt-4 space-y-4">
              {BINDINGS.map((binding) => (
                <ThesisPriceRuleEditor
                  key={binding}
                  degree={degree}
                  binding={binding}
                  draft={draftFor(degree, binding)}
                  saving={savingKey === ruleKey(degree, binding)}
                  locked={savingKey !== null && savingKey !== ruleKey(degree, binding)}
                  onChange={(patch) => updateDraft(degree, binding, patch)}
                  onSave={() => save(degree, binding)}
                />
              ))}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </PageContainer>
  );
}
